import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { type BarItem, BarList } from "@/components/charts/BarList";
import { DonutChart, type DonutSlice } from "@/components/charts/DonutChart";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { ratingColorVar } from "@/components/shared/rating-scale";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useReviewCycles } from "@/hooks/use-review-cycles";
import {
  type CriterionScoreGroup,
  useCycleEndorsements,
  useEndorsementRankings,
  useTopPerformersByCriterion,
} from "@/hooks/use-review-recognition-report";
import { categoryColorVar } from "@/lib/category-colors";

// Cycled for criteria that carry no skill category of their own, so the
// donut and the per-criterion headers always have a color to show — same
// brand ramp BarList defaults to.
const CRITERION_FALLBACK_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

/**
 * Recognition surfaced across one or more rounds — DM-WF-001, EPIC-074.
 *
 * HR/admin-only, same tier as the competency report. Defaults to "All time"
 * (every round, closed or still open) rather than requiring a pick first —
 * the point of this view is the pattern across someone's whole tenure, not a
 * single round in isolation. Picking specific rounds narrows it the same way
 * the competency report's cycle checkboxes do.
 */
export function RecognitionReport() {
  const [selectedCycleIds, setSelectedCycleIds] = useState<string[]>([]);
  const { data: cycles = [] } = useReviewCycles();
  const isAllTime = selectedCycleIds.length === 0;

  const endorsements = useCycleEndorsements(selectedCycleIds);
  const rankings = useEndorsementRankings(selectedCycleIds);
  const topPerformers = useTopPerformersByCriterion(selectedCycleIds);

  function toggleCycle(cycleId: string) {
    setSelectedCycleIds((ids) =>
      ids.includes(cycleId) ? ids.filter((id) => id !== cycleId) : [...ids, cycleId],
    );
  }

  return (
    <div className="space-y-6">
      <DetailSection title="Choose what to report on">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={isAllTime ? "default" : "outline"}
            size="sm"
            aria-pressed={isAllTime}
            onClick={() => setSelectedCycleIds([])}
          >
            All time
          </Button>
          {cycles.map((cycle) => {
            const active = selectedCycleIds.includes(cycle.id);
            return (
              <Button
                key={cycle.id}
                type="button"
                variant={active ? "default" : "outline"}
                size="sm"
                aria-pressed={active}
                onClick={() => toggleCycle(cycle.id)}
              >
                {cycle.name}
                {cycle.statusKey !== "closed" && (
                  <span className="ml-1 text-xs opacity-75">(open)</span>
                )}
              </Button>
            );
          })}
        </div>
      </DetailSection>

      <SummaryTiles
        endorsementCount={endorsements.data?.length}
        peopleRecognized={rankings.data?.length}
        roundsIncluded={isAllTime ? cycles.length : selectedCycleIds.length}
      />

      <AtAGlance endorsements={endorsements} rankings={rankings} />
      <TopPerformers topPerformers={topPerformers} />
      <AllEndorsements
        endorsements={endorsements}
        showRound={isAllTime || selectedCycleIds.length > 1}
      />
    </div>
  );
}

function SummaryTiles({
  endorsementCount,
  peopleRecognized,
  roundsIncluded,
}: {
  endorsementCount: number | undefined;
  peopleRecognized: number | undefined;
  roundsIncluded: number;
}) {
  const tiles: { n: number | undefined; label: string }[] = [
    { n: endorsementCount, label: "Endorsements given" },
    { n: peopleRecognized, label: "People recognized" },
    { n: roundsIncluded, label: "Rounds included" },
  ];
  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
      {tiles.map((tile) => (
        <div key={tile.label} className="flex flex-col rounded-[10px] border bg-card px-4 py-3.5">
          {tile.n === undefined ? (
            <Skeleton className="h-8 w-14" />
          ) : (
            <span className="font-display text-2xl font-bold leading-none text-card-foreground tabular-nums">
              {tile.n.toLocaleString()}
            </span>
          )}
          <span className="mt-1.5 text-xs font-medium text-muted-foreground">{tile.label}</span>
        </div>
      ))}
    </div>
  );
}

function AtAGlance({
  endorsements,
  rankings,
}: {
  endorsements: ReturnType<typeof useCycleEndorsements>;
  rankings: ReturnType<typeof useEndorsementRankings>;
}) {
  const isLoading = endorsements.isLoading || rankings.isLoading;
  const isError = endorsements.isError || rankings.isError;

  const criterionSlices = useMemo((): DonutSlice[] => {
    const rows = endorsements.data ?? [];
    const byTenet = new Map<string, { count: number; color: string }>();
    let colorIndex = 0;
    for (const row of rows) {
      const existing = byTenet.get(row.tenetTitle);
      if (existing) {
        existing.count += 1;
        continue;
      }
      // Tenets carry no category of their own (unlike a competency), so this
      // chart always cycles the fallback palette rather than reading a color.
      const color =
        CRITERION_FALLBACK_COLORS[colorIndex % CRITERION_FALLBACK_COLORS.length] ??
        "var(--chart-1)";
      colorIndex += 1;
      byTenet.set(row.tenetTitle, { count: 1, color });
    }
    return [...byTenet.entries()]
      .map(([label, { count, color }]) => ({ label, value: count, color }))
      .sort((a, b) => b.value - a.value);
  }, [endorsements.data]);

  const rankingItems = useMemo((): BarItem[] => {
    return (rankings.data ?? [])
      .slice(0, 8)
      .map((r) => ({ label: r.subjectName, value: r.totalEndorsements, color: "var(--chart-1)" }));
  }, [rankings.data]);

  return (
    <DetailSection title="Recognition at a glance">
      {isError ? (
        <ErrorState
          message="Couldn't load the recognition summary"
          onRetry={() => {
            endorsements.refetch();
            rankings.refetch();
          }}
        />
      ) : isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : criterionSlices.length === 0 ? (
        <EmptyState
          title="Nothing to show yet"
          description="No submitted reviews in the selected rounds have endorsed anyone."
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Endorsements by tenet
            </h3>
            <DonutChart
              title="Endorsements by tenet"
              slices={criterionSlices}
              centerLabel={
                criterionSlices.reduce((n, s) => n + s.value, 0) === 1
                  ? "endorsement"
                  : "endorsements"
              }
            />
          </div>
          <div className="md:border-l md:pl-6">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              People ranked by endorsements
            </h3>
            {rankingItems.length > 0 ? (
              <BarList title="People ranked by endorsements received" items={rankingItems} />
            ) : (
              <p className="text-sm text-muted-foreground">Nobody has been endorsed yet.</p>
            )}
          </div>
        </div>
      )}
    </DetailSection>
  );
}

function TopPerformers({
  topPerformers,
}: {
  topPerformers: ReturnType<typeof useTopPerformersByCriterion>;
}) {
  const { data, isLoading, isError, error, refetch } = topPerformers;

  return (
    <DetailSection title="Top performers by question">
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load top performers"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="Nothing to report yet"
          description="No submitted ratings in the selected rounds have an answer other than Not applicable."
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {data.map((group, index) => (
            <CriterionGroup key={group.questionTitle} group={group} fallbackIndex={index} />
          ))}
        </div>
      )}
    </DetailSection>
  );
}

function CriterionGroup({
  group,
  fallbackIndex,
}: {
  group: CriterionScoreGroup;
  fallbackIndex: number;
}) {
  const headerColor =
    categoryColorVar(group.categoryColor) ??
    CRITERION_FALLBACK_COLORS[fallbackIndex % CRITERION_FALLBACK_COLORS.length] ??
    "var(--chart-1)";

  const items: BarItem[] = group.topPerformers.map((p) => ({
    label: p.subjectName,
    value: p.averageOrdinal,
    color: ratingColorVar(p.averageOrdinal),
  }));

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-1.5">
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: headerColor }}
          aria-hidden
        />
        <h3 className="font-medium text-sm">{group.questionTitle}</h3>
      </div>
      <BarList
        title={`Top performers — ${group.questionTitle}`}
        items={items}
        max={5}
        formatValue={(n) => n.toFixed(1)}
      />
    </div>
  );
}

function AllEndorsements({
  endorsements,
  showRound,
}: {
  endorsements: ReturnType<typeof useCycleEndorsements>;
  showRound: boolean;
}) {
  const { data, isLoading, isError, error, refetch } = endorsements;

  return (
    <DetailSection title="All endorsements">
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load endorsements"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="No endorsements yet"
          description="Nobody who has handed in their review has endorsed a question in the selected rounds."
        />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {data.map((row) => (
            <li key={row.id} className="space-y-1.5 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <Link
                    to="/dashboard/persons/$personId"
                    params={{ personId: row.subjectPersonId }}
                    className="font-medium text-sm hover:underline"
                  >
                    {row.subjectName}
                  </Link>
                  {showRound && (
                    <Badge variant="secondary" className="font-normal">
                      {row.cycleName}
                    </Badge>
                  )}
                </div>
                <span className="text-muted-foreground text-xs">
                  {row.endorserName} · {row.relationshipName}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-foreground text-sm">
                <span className="font-medium">{row.tenetTitle}</span>
              </div>
              <p className="text-muted-foreground text-sm">{row.detail}</p>
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}
